import type { PlainMessage } from '@bufbuild/protobuf';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { FC, ReactNode, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  components,
  type DropdownIndicatorProps,
  type StylesConfig,
} from 'react-select';

import {
  NoteTableCellType,
  type NoteCodeSystem,
  type NoteTableColumn,
  type NoteTableRow,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  Button,
  Checkbox,
  ChipGroup,
  Icon,
  Select,
  Tooltip,
} from '@/shared/ui';

import { NOTE_CODE_SYSTEMS } from '@/entities/templates';

import { i18n, NOTE_TABLE_CELL_TYPES, NOTE_UNITS } from '../config';
import {
  findNoteCodeProblem,
  normalizeNoteValue,
  nextNoteTableCode,
  setNoteTableAddsRows,
  setNoteTableColumns,
  setNoteTableRows,
  type NoteBlockDef,
  type NoteCodeProblem,
} from '../lib';

import styles from './FieldSettings.module.scss';

// The settings a field takes by its value type that a field's pop-up
// shows: the explicit none, a number's unit, and a table's columns and
// rows.

export const SettingsField: FC<{ label: ReactNode; children: ReactNode }> = ({
  label,
  children,
}) => (
  <label className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    {children}
  </label>
);

const SettingsSelectIndicator = (props: DropdownIndicatorProps) => (
  <components.DropdownIndicator {...props}>
    <Icon name="arrowDown" size={16} />
  </components.DropdownIndicator>
);

// The app's select at the settings' size: 40px, the chevron 14px from the
// right edge, a menu of its own.
const SETTINGS_SELECT_STYLES: StylesConfig = {
  container: (base) => ({ ...base, fontSize: '14px', lineHeight: '20px' }),
  control: (base, { isFocused }) => ({
    ...base,
    minHeight: 40,
    backgroundColor: 'var(--bg1)',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: isFocused ? 'var(--purpleflat)' : 'var(--ws-violet-line)',
    boxShadow: 'none',
    cursor: 'pointer',
    ':hover': { borderColor: 'var(--purpleflat)' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 12px', margin: 0 }),
  singleValue: (base) => ({ ...base, margin: 0, color: 'var(--bw)' }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: 'var(--bw)' }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 0,
    marginRight: 14,
    color: 'var(--purpletext1)',
  }),
  // The menu opens in the settings' flow, under the select, so the
  // settings scroll to it and never clip it.
  menu: (base) => ({
    ...base,
    position: 'relative',
    marginTop: 6,
    backgroundColor: 'var(--bg1)',
    borderRadius: 10,
    boxShadow:
      '0 0 0 1px var(--ws-violet-line), 0 8px 24px rgba(var(--purpletext1-rgb), 0.12)',
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, { isSelected, isFocused, isDisabled }) => ({
    ...base,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: isDisabled ? 'default' : 'pointer',
    color: isSelected ? 'var(--purpletext1)' : 'var(--bw)',
    backgroundColor:
      isSelected || isFocused ? 'var(--ws-violet-fill)' : 'transparent',
    ':active': { backgroundColor: 'var(--ws-violet-fill)' },
  }),
};

export type SettingsSelectOption<T> = {
  value: T;
  label: string;
  isDisabled?: boolean;
};

type SettingsSelectProps<T> = {
  value: T;
  options: SettingsSelectOption<T>[];
  // Shown over the select; without it, `ariaLabel` names the select.
  label?: ReactNode;
  ariaLabel?: string;
  className?: string;
  onChange: (value: T) => void;
};

// A setting chosen from a list: its label over the app's select, which the
// label names.
export const SettingsSelect = <T extends string | number>({
  value,
  options,
  label,
  ariaLabel,
  className,
  onChange,
}: SettingsSelectProps<T>) => {
  const labelId = useId();
  return (
    <div className={cn(styles.field, className)}>
      {label && (
        <span id={labelId} className={styles.fieldLabel}>
          {label}
        </span>
      )}
      <Select
        aria-labelledby={label ? labelId : undefined}
        aria-label={ariaLabel}
        options={options}
        value={value}
        isSearchable={false}
        styles={SETTINGS_SELECT_STYLES}
        components={{ DropdownIndicator: SettingsSelectIndicator }}
        onChange={(next) => onChange(next as T)}
      />
    </div>
  );
};

type CommitInputProps = {
  value: string;
  label?: string;
  className?: string;
  placeholder?: string;
  // An emptied box is restored instead of applied.
  required?: boolean;
  readOnly?: boolean;
  title?: string;
  // The id of the text that says what is wrong with the value.
  problemId?: string;
  onCommit: (value: string) => void;
};

// A text box that applies on Enter or on leaving it and restores what it
// held on Escape, so a value is applied once, not on every key.
const CommitInput: FC<CommitInputProps> = ({
  value,
  label,
  className,
  placeholder,
  required = false,
  readOnly = false,
  title,
  problemId,
  onCommit,
}) => {
  const [text, setText] = useState(value);
  const [held, setHeld] = useState(value);
  if (held !== value) {
    setHeld(value);
    setText(value);
  }
  const commit = () => {
    const next = text.trim();
    if (next === value || (required && !next)) {
      setText(value);
      return;
    }
    onCommit(next);
  };

  return (
    <input
      className={cn('p3', styles.optionInput, className)}
      aria-label={label}
      aria-invalid={problemId ? true : undefined}
      aria-describedby={problemId}
      placeholder={placeholder}
      readOnly={readOnly}
      title={title}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setText(value);
        }
      }}
    />
  );
};

const CodeProblem: FC<{
  id: string;
  problem: NoteCodeProblem;
  codeSystem?: NoteCodeSystem;
}> = ({ id, problem, codeSystem }) => {
  const system = codeSystem ? NOTE_CODE_SYSTEMS[codeSystem] : undefined;
  const message = () => {
    switch (problem) {
      case 'missing':
        return (
          <FormattedMessage
            id="workspace.blockEditor.codeMissing"
            defaultMessage="A code is needed"
          />
        );
      case 'repeated':
        return (
          <FormattedMessage
            id="workspace.blockEditor.codeRepeated"
            defaultMessage="This code is already used"
          />
        );
      default:
        return system ? (
          <FormattedMessage
            id="workspace.blockEditor.codeFormatSystem"
            defaultMessage="A code of {system} as written, e.g. {example}"
            values={{ system: system.name, example: system.example }}
          />
        ) : (
          <FormattedMessage
            id="workspace.blockEditor.codeFormat"
            defaultMessage="Lowercase Latin letters, digits and _, starting with a letter"
          />
        );
    }
  };
  return (
    <p id={id} className={cn('p4', styles.issue, styles.codeProblem)}>
      {message()}
    </p>
  );
};

type CodedItem = { Code: string; Label: string };

type CodedRowTexts = {
  item: (number: number) => string;
  code: (number: number) => string;
  newItem: string;
  addShort: string;
  add: ReactNode;
};

type CodedRowsProps<T extends CodedItem> = {
  items: T[];
  codeSystem?: NoteCodeSystem;
  // A tooth code always has a code; an option has one when the others do.
  codeRequired?: boolean;
  // The codes the active version gives by label: such a code stays.
  frozenCodes: Map<string, string>;
  texts: CodedRowTexts;
  // A new item from its label, among the items there are.
  create: (Label: string, items: T[]) => T;
  details?: (item: T, set: (patch: Partial<T>) => void) => ReactNode;
  onChange: (items: T[], kept: (number | null)[]) => void;
};

// Items with a code and a label, in order: dragged to reorder, the code and
// the label set in place, more under "Details", removed with × and added at
// the end. A label the active version gives a code takes that code, which
// is then not changed here.
const CodedRows = <T extends CodedItem>({
  items,
  codeSystem,
  codeRequired = false,
  frozenCodes,
  texts,
  create,
  details,
  onChange,
}: CodedRowsProps<T>) => {
  const { formatMessage } = useIntl();
  const baseId = useId();
  const [open, setOpen] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const ids = items.map((_, index) => `${baseId}-${index}`);
  const same = items.map((_, index) => index);
  const frozen = (Label: string) => frozenCodes.get(normalizeNoteValue(Label));
  const set = (index: number, patch: Partial<T>) =>
    onChange(
      items.map((item, at) => {
        if (at !== index) {
          return item;
        }
        const next = { ...item, ...patch };
        const code = patch.Label === undefined ? undefined : frozen(next.Label);
        return code ? { ...next, Code: code } : next;
      }),
      same,
    );
  const remove = (index: number) => {
    setOpen((current) => {
      if (current === null || current < index) {
        return current;
      }
      return current === index ? null : current - 1;
    });
    onChange(
      items.filter((_, at) => at !== index),
      same.filter((at) => at !== index),
    );
  };
  const move = (from: number, to: number) => {
    setOpen((current) => (current === from ? to : current));
    onChange(arrayMove(items, from, to), arrayMove(same, from, to));
  };
  const taken = (Label: string) =>
    items.some(
      (item) => normalizeNoteValue(item.Label) === normalizeNoteValue(Label),
    );
  const add = () => {
    const Label = draft.trim();
    if (!Label || taken(Label)) {
      return;
    }
    const item = create(Label, items);
    const code = frozen(Label);
    onChange(
      [...items, code ? { ...item, Code: code } : item],
      [...same, null],
    );
    setDraft('');
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (over && active.id !== over.id) {
            move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
          }
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ol className={styles.options}>
            {items.map((item, index) => {
              const problem = findNoteCodeProblem(items, index, {
                codeSystem,
                required: codeRequired,
              });
              const isFrozen = !!item.Code && frozen(item.Label) === item.Code;
              return (
                <CodedRow
                  key={ids[index]}
                  id={ids[index]}
                  label={item.Label}
                  open={open === index}
                  onToggle={() =>
                    setOpen((current) => (current === index ? null : index))
                  }
                  onRemove={() => remove(index)}
                  details={details?.(item, (patch) => set(index, patch))}
                  problem={
                    problem && (
                      <CodeProblem
                        id={`${ids[index]}-problem`}
                        problem={problem}
                        codeSystem={codeSystem}
                      />
                    )
                  }
                >
                  <CommitInput
                    className={styles.codeInput}
                    value={item.Code}
                    label={texts.code(index + 1)}
                    placeholder={
                      codeSystem
                        ? NOTE_CODE_SYSTEMS[codeSystem]?.example
                        : formatMessage({
                            id: 'workspace.blockEditor.codePlaceholder',
                            defaultMessage: 'code',
                          })
                    }
                    readOnly={isFrozen}
                    title={
                      isFrozen
                        ? formatMessage({
                            id: 'workspace.blockEditor.codeFrozen',
                            defaultMessage:
                              'The published version holds this code for the label, so it stays',
                          })
                        : undefined
                    }
                    problemId={problem ? `${ids[index]}-problem` : undefined}
                    onCommit={(Code) => set(index, { Code } as Partial<T>)}
                  />
                  <CommitInput
                    value={item.Label}
                    label={texts.item(index + 1)}
                    required
                    onCommit={(Label) => set(index, { Label } as Partial<T>)}
                  />
                </CodedRow>
              );
            })}
          </ol>
        </SortableContext>
      </DndContext>
      <div className={styles.values}>
        <input
          className={cn('p4', styles.addInput)}
          aria-label={texts.newItem}
          placeholder={texts.addShort}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button
          variant="tertiary"
          size="small"
          icon="plus"
          disabled={!draft.trim() || taken(draft)}
          onClick={add}
        >
          {texts.add}
        </Button>
      </div>
    </>
  );
};

type CodedRowProps = {
  id: string;
  label: string;
  open: boolean;
  problem: ReactNode;
  // Without details the row opens none.
  details?: ReactNode;
  children: ReactNode;
  onToggle: () => void;
  onRemove: () => void;
};

const CodedRow: FC<CodedRowProps> = ({
  id,
  label,
  open,
  problem,
  details,
  children,
  onToggle,
  onRemove,
}) => {
  const { formatMessage } = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(styles.codedItem, isDragging && styles.optionDragging)}
    >
      <div className={styles.codedLine}>
        <button
          type="button"
          className={styles.optionGrip}
          aria-label={formatMessage(
            {
              id: 'workspace.blockEditor.moveOption',
              defaultMessage: 'Move “{value}”',
            },
            { value: label },
          )}
          {...attributes}
          {...listeners}
        >
          <span className={styles.gripDots} aria-hidden />
        </button>
        {children}
        {details !== undefined && (
          <button
            type="button"
            className={cn(styles.pillRemove, open && styles.detailsOpen)}
            aria-label={formatMessage(
              {
                id: 'workspace.blockEditor.optionDetails',
                defaultMessage: 'Details of “{value}”',
              },
              { value: label },
            )}
            aria-expanded={open}
            aria-controls={`${id}-details`}
            onClick={onToggle}
          >
            <Icon name="arrowDown" size={14} />
          </button>
        )}
        <button
          type="button"
          className={styles.pillRemove}
          aria-label={formatMessage(
            {
              id: 'workspace.template.removeOption',
              defaultMessage: 'Remove {value}',
            },
            { value: label },
          )}
          onClick={onRemove}
        >
          <Icon name="close" size={14} />
        </button>
      </div>
      {problem}
      {open && details !== undefined && (
        <div id={`${id}-details`} className={styles.codedDetails}>
          {details}
        </div>
      )}
    </li>
  );
};

type ModeSwitchProps = {
  label: string;
  offLabel: ReactNode;
  onLabel: ReactNode;
  help: ReactNode;
  on: boolean;
  onChange: (on: boolean) => void;
};

// A setting of two modes, both in sight and the active one filled; the «?»
// after it explains both, so no tooltip covers the modes while one is chosen.
export const ModeSwitch: FC<ModeSwitchProps> = ({
  label,
  offLabel,
  onLabel,
  help,
  on,
  onChange,
}) => {
  const { formatMessage } = useIntl();
  return (
    <div className={styles.modeSwitch}>
      <ChipGroup<'off' | 'on'>
        variant="segmented"
        label={label}
        items={[
          { value: 'off', label: offLabel },
          { value: 'on', label: onLabel },
        ]}
        value={on ? 'on' : 'off'}
        onChange={(mode) => {
          if ((mode === 'on') !== on) {
            onChange(mode === 'on');
          }
        }}
      />
      <Tooltip.Primary
        content={help}
        triggerClassName={styles.modeHelp}
        triggerProps={{
          'aria-label': formatMessage({
            id: 'workspace.templateEdit.modeHelp',
            defaultMessage: 'How it works',
          }),
          role: 'img',
          tabIndex: 0,
        }}
      >
        <Icon name="help" size={16} />
      </Tooltip.Primary>
    </div>
  );
};

type NoneValue = { AllowNone?: boolean; NoneLabel?: string };

// The explicit "none", told apart from an empty field, and how it prints;
// without a label of its own it prints the template language's word.
export const NoneSettings: FC<{
  value: NoneValue;
  onChange: (value: NoneValue) => void;
}> = ({ value, onChange }) => {
  const { formatMessage } = useIntl();
  return (
    <>
      <ModeSwitch
        label={formatMessage({
          id: 'workspace.templateEdit.noneMode',
          defaultMessage: 'Answer “no”',
        })}
        offLabel={formatMessage({
          id: 'workspace.templateEdit.noneOff',
          defaultMessage: 'Without “no”',
        })}
        onLabel={formatMessage({
          id: 'workspace.templateEdit.noneOn',
          defaultMessage: 'May be answered “no”',
        })}
        help={formatMessage({
          id: 'workspace.templateEdit.noneModeHelp',
          defaultMessage:
            'Without “no” — if the clinician said “no” or “not bothering”, the field stays empty. May be answered “no” — an explicit “no” is recorded with your label (for example, “denies”) and excludes the field’s other values.',
        })}
        on={!!value.AllowNone}
        onChange={(on) =>
          onChange(
            on
              ? { AllowNone: true, NoneLabel: value.NoneLabel }
              : { AllowNone: undefined, NoneLabel: undefined },
          )
        }
      />
      {value.AllowNone && (
        <SettingsField
          label={
            <FormattedMessage
              id="workspace.blockEditor.noneLabel"
              defaultMessage="“None” prints as"
            />
          }
        >
          <input
            className={styles.select}
            placeholder={formatMessage({
              id: 'workspace.blockEditor.noneLabelPlaceholder',
              defaultMessage: 'the word for “none”',
            })}
            value={value.NoneLabel ?? ''}
            onChange={(event) =>
              onChange({
                AllowNone: true,
                NoneLabel: event.target.value || undefined,
              })
            }
          />
        </SettingsField>
      )}
    </>
  );
};

export const UnitSelect: FC<{
  value?: string;
  optional: boolean;
  onChange: (value: string | undefined) => void;
}> = ({ value, optional, onChange }) => {
  const { formatMessage } = useIntl();
  return (
    <SettingsSelect
      label={
        <FormattedMessage
          id="workspace.blockEditor.unit"
          defaultMessage="Unit"
        />
      }
      value={value ?? ''}
      options={[
        ...(optional
          ? [
              {
                value: '',
                label: formatMessage({
                  id: 'workspace.blockEditor.unit.none',
                  defaultMessage: 'No unit',
                }),
              },
            ]
          : []),
        ...(value && !NOTE_UNITS.includes(value)
          ? [{ value, label: value }]
          : []),
        ...NOTE_UNITS.map((unit) => ({
          value: unit,
          label: formatMessage(
            i18n.noteUnitMessages[unit as keyof typeof i18n.noteUnitMessages],
          ),
        })),
      ]}
      onChange={(unit) => onChange(unit || undefined)}
    />
  );
};

const NOTE_PRECISIONS = [0, 1, 2, 3];

// Zero digits is stored as none.
const PrecisionSelect: FC<{
  value?: number;
  onChange: (value: number | undefined) => void;
}> = ({ value, onChange }) => (
  <SettingsSelect
    label={
      <FormattedMessage
        id="workspace.blockEditor.precision"
        defaultMessage="Digits after the point"
      />
    }
    value={value ?? 0}
    options={NOTE_PRECISIONS.map((precision) => ({
      value: precision,
      label: String(precision),
    }))}
    onChange={(precision) => onChange(precision || undefined)}
  />
);

const NO_CODES = new Map<string, string>();

// A table's columns, each with the type of its cells (a number's unit and
// digits under its details), and its fixed rows, or rows the note adds.
// A new column or row takes the next free code ("c3", "r2").
export const TableSettings: FC<{
  block: NoteBlockDef;
  onChange: (block: NoteBlockDef) => void;
}> = ({ block, onChange }) => {
  const { formatMessage } = useIntl();
  if (block.Kind.case !== 'Table') {
    return null;
  }
  const value = block.Kind.value;
  const number = NoteTableCellType.NoteTableCellTypeNumber;

  return (
    <>
      <div className={styles.field} data-component="workspace-template-columns">
        <span className={styles.fieldLabel}>
          <FormattedMessage
            id="workspace.blockEditor.columns"
            defaultMessage="Columns"
          />
        </span>
        <CodedRows<PlainMessage<NoteTableColumn>>
          items={value.Columns}
          codeRequired
          frozenCodes={NO_CODES}
          texts={{
            item: (count) =>
              formatMessage(
                {
                  id: 'workspace.blockEditor.column',
                  defaultMessage: 'Column {number}',
                },
                { number: count },
              ),
            code: (count) =>
              formatMessage(
                {
                  id: 'workspace.blockEditor.columnCode',
                  defaultMessage: 'Code of column {number}',
                },
                { number: count },
              ),
            newItem: formatMessage({
              id: 'workspace.blockEditor.newColumn',
              defaultMessage: 'New column',
            }),
            addShort: formatMessage({
              id: 'workspace.blockEditor.addColumnShort',
              defaultMessage: '+ column',
            }),
            add: (
              <FormattedMessage
                id="workspace.blockEditor.addColumn"
                defaultMessage="Add column"
              />
            ),
          }}
          create={(Label, columns) => ({
            Code: nextNoteTableCode('c', columns),
            Label,
            Type: NoteTableCellType.NoteTableCellTypeCheckbox,
          })}
          details={(column, set) => (
            <>
              <SettingsSelect
                label={
                  <FormattedMessage
                    id="workspace.blockEditor.cellType"
                    defaultMessage="A cell holds"
                  />
                }
                value={column.Type}
                options={NOTE_TABLE_CELL_TYPES.map((type) => ({
                  value: type,
                  label: formatMessage(i18n.noteTableCellTypeMessages[type]),
                }))}
                onChange={(Type) =>
                  set({ Type, Unit: undefined, Precision: undefined })
                }
              />
              {column.Type === number && (
                <>
                  <UnitSelect
                    value={column.Unit}
                    optional
                    onChange={(Unit) => set({ Unit })}
                  />
                  <PrecisionSelect
                    value={column.Precision}
                    onChange={(Precision) => set({ Precision })}
                  />
                </>
              )}
            </>
          )}
          onChange={(Columns, kept) =>
            onChange(setNoteTableColumns(block, Columns, kept))
          }
        />
      </div>
      <Checkbox
        className={cn('p4', styles.multiple)}
        checked={!!value.AddRows}
        onChange={() => onChange(setNoteTableAddsRows(block, !value.AddRows))}
      >
        <FormattedMessage
          id="workspace.blockEditor.addRows"
          defaultMessage="Rows are added on the note"
        />
      </Checkbox>
      {!value.AddRows && (
        <div className={styles.field} data-component="workspace-template-rows">
          <span className={styles.fieldLabel}>
            <FormattedMessage
              id="workspace.blockEditor.rows"
              defaultMessage="Rows"
            />
          </span>
          <CodedRows<PlainMessage<NoteTableRow>>
            items={value.Rows}
            codeRequired
            frozenCodes={NO_CODES}
            texts={{
              item: (count) =>
                formatMessage(
                  {
                    id: 'workspace.blockEditor.row',
                    defaultMessage: 'Row {number}',
                  },
                  { number: count },
                ),
              code: (count) =>
                formatMessage(
                  {
                    id: 'workspace.blockEditor.rowCode',
                    defaultMessage: 'Code of row {number}',
                  },
                  { number: count },
                ),
              newItem: formatMessage({
                id: 'workspace.blockEditor.newRow',
                defaultMessage: 'New row',
              }),
              addShort: formatMessage({
                id: 'workspace.blockEditor.addRowShort',
                defaultMessage: '+ row',
              }),
              add: (
                <FormattedMessage
                  id="workspace.blockEditor.addRow"
                  defaultMessage="Add row"
                />
              ),
            }}
            create={(Label, rows) => ({
              Code: nextNoteTableCode('r', rows),
              Label,
            })}
            onChange={(Rows, kept) =>
              onChange(setNoteTableRows(block, Rows, kept))
            }
          />
        </div>
      )}
    </>
  );
};
